import { useMemo } from "react";
import { useHyperLedger } from "#/hooks/use-hyperliquid-account";
import { useWalletTransfers } from "#/hooks/use-wallet-transfers";
import {
	buildTransactionRows,
	type LedgerInput,
} from "../lib/transaction-history-rows";

/**
 * The portfolio's "Deposit & Withdrawal" list: Hyperliquid's ledger plus the
 * backend-executed transfers HL never sees. Merge rules and the reasoning
 * behind them live in `lib/transaction-history-rows.ts`.
 *
 * `useWalletTransfers` is already mounted app-wide by `TransferStatusSync`, so
 * this shares that cache entry — no extra requests, and it polls every 5s only
 * while something is actually in flight.
 */
export function useTransactionHistory() {
	const ledgerQuery = useHyperLedger();
	const transfersQuery = useWalletTransfers();

	const rows = useMemo(() => {
		const ledger: LedgerInput[] = ledgerQuery.ledger.map((item) => ({
			time: item.time,
			hash: item.hash,
			type: item.delta.type === "deposit" ? "deposit" : "withdraw",
			usdc:
				item.delta.type === "deposit" || item.delta.type === "withdraw"
					? item.delta.usdc
					: "0",
			fee: item.delta.type === "withdraw" ? item.delta.fee : null,
		}));
		return buildTransactionRows(ledger, transfersQuery.data);
	}, [ledgerQuery.ledger, transfersQuery.data]);

	return {
		rows,
		// Only the ledger gates the spinner. Transfers is small, fast, and
		// polls on a 5s timer — gating on it would flash the whole table.
		isLoading:
			!ledgerQuery.isFetched &&
			(ledgerQuery.isPending || ledgerQuery.isFetching),
		// Both sources must fail before the table blanks out: one being down
		// is no reason to hide what the other still knows.
		isError: ledgerQuery.isError && transfersQuery.isError,
		refetch: () => {
			void ledgerQuery.refetch();
			void transfersQuery.refetch();
		},
	};
}
